/**
 * Copy a master record, or start its next version — on the record list itself.
 *
 * 2026-10-06 · the list's "Copy this record" and "New version" handed off to the legacy MBMR
 * page to open its dialog there, so the person was taken away from the list they were working
 * in. The same dialog now opens here, makes the same call (`copy_recipe_data`) with the same
 * rules — a copy needs a new name; a new version needs a change reason and an approved Change
 * Control Request — and the list reloads in place.
 */
import React, { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Select, message } from "antd";
import { copy_recipe_data } from "@/utils/api";
import ChangeControlPanel from "@/components/ChangeControlPanel";
import { useMbmrAlias } from "@/hooks/useAppSetting";

export default function CopyVersionModal({ record, mode, onClose, onDone }) {
  const mbmrAlias = useMbmrAlias();
  const versioning = mode === "version";
  const [name, setName] = useState("");
  const [changeControl, setChangeControl] = useState("");
  const [reason, setReason] = useState("");
  const [openCcr, setOpenCcr] = useState(null);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  // Shown with the version request, as the legacy dialog shows them; not part of the save.
  const [changeTitle, setChangeTitle] = useState("");
  const [changeType, setChangeType] = useState(`${mbmrAlias} Change`);
  const [changeCategory, setChangeCategory] = useState("Minor");
  const [department, setDepartment] = useState("Production");

  useEffect(() => {
    setName(""); setChangeControl(""); setReason(""); setOpenCcr(null); setTried(false); setBusy(false);
    setChangeTitle("");
  }, [record?.id, mode]);

  if (!record || !mode) return null;
  const version = record.major_version != null ? `v${record.major_version}.${record.minor_version ?? 0}` : "—";
  const nameMissing = !versioning && !name.trim();
  const reasonMissing = versioning && !reason.trim();

  const save = () => {
    setTried(true);
    if (nameMissing || reasonMissing) return;
    if (versioning && !openCcr) {
      message.warning("Raise and get a Change Control Request approved first — see Change Control.");
      return;
    }
    const req = {
      recipe_id: record.id,
      new_version: versioning,
      ...(versioning
        ? { change_reason: reason.trim(), change_control_id: openCcr.ccr_no }
        : { new_recipe_name: name.trim(), change_control_id: changeControl.trim() || null }),
    };
    setBusy(true);
    // The callback runs only on success: a refusal (a name already taken — 409, no approved
    // CCR — 400) is shown by the shared response handler and never reaches it. So the button
    // is released once the call settles, whatever it answered, never from inside the callback.
    copy_recipe_data(req, (res) => {
      if (res?.status === "success") {
        message.success(res?.message || (versioning ? "New version created." : "Record copied."));
        onDone?.(res?.data);
      } else {
        message.warning(res?.message || "That could not be done.");
      }
    })
      .catch(() => message.error("The server could not be reached. Try again."))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      open
      width={versioning ? 900 : 520}
      title={versioning ? `New version of ${record.recipe_code || record.name}` : `Copy ${record.recipe_code || record.name}`}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="save" type="primary" loading={busy} onClick={save} disabled={versioning && !openCcr}
                title={versioning && !openCcr ? "Raise and get a Change Control Request approved first" : undefined}>
          Save
        </Button>,
      ]}
    >
      {versioning ? (
        <div className="mb-4 pb-3 border-b border-gray-200 flex flex-col gap-1">
          <div className="flex gap-2"><span className="font-semibold">{mbmrAlias} Name:</span><span>{record.name || "-"}</span></div>
          <div className="flex gap-2"><span className="font-semibold">Current {mbmrAlias} Version:</span><span>{version}</span></div>
        </div>
      ) : null}
      {versioning ? (
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label>Change Control</label>
              <ChangeControlPanel targetObjectType="Recipe" targetId={record.id} onOpenCcrChange={setOpenCcr} />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="cv_title">Change Title</label>
              <Input id="cv_title" value={changeTitle} onChange={(e) => setChangeTitle(e.target.value)} />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="cv_type">Change Type</label>
              <Select id="cv_type" value={changeType} onChange={setChangeType}
                      options={[`${mbmrAlias} Change`, "Process Change", "Equipment Change", "Material Change"].map((v) => ({ value: v, label: v }))} />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="cv_cat">Change Category</label>
              <Select id="cv_cat" value={changeCategory} onChange={setChangeCategory}
                      options={["Major", "Minor"].map((v) => ({ value: v, label: v }))} />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="cv_dep">Department</label>
              <Select id="cv_dep" value={department} onChange={setDepartment}
                      options={["Production", "Quality", "R&D", "Engineering"].map((v) => ({ value: v, label: v }))} />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="cv_reason">Change Reason<span className="text-red-500"> *</span></label>
              <Form.Item validateStatus={tried && reasonMissing ? "error" : ""} help={tried && reasonMissing ? "Change Reason is required" : null}
                         style={{ marginBottom: 0 }}>
                <Input.TextArea id="cv_reason" rows={3} placeholder="Enter Change Reason" value={reason}
                                onChange={(e) => setReason(e.target.value)} />
              </Form.Item>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="cv_name">{mbmrAlias} Name<span className="text-red-500"> *</span></label>
            <Form.Item validateStatus={tried && nameMissing ? "error" : ""} help={tried && nameMissing ? `Enter the new ${mbmrAlias} name` : null}
                       style={{ marginBottom: 0 }}>
              <Input id="cv_name" autoFocus placeholder={`Enter ${mbmrAlias} Name`} value={name}
                     onChange={(e) => setName(e.target.value)} onPressEnter={save} />
            </Form.Item>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="cv_cc">Change Control</label>
            <Input id="cv_cc" placeholder="Enter Change Control" value={changeControl}
                   onChange={(e) => setChangeControl(e.target.value)} />
          </div>
        </div>
      )}
    </Modal>
  );
}
